import React, { useState, useEffect } from 'react';
import {
  listAssistantIds,
  getFullInventory,
  checkInventoryUrls,
  getAllScrapeJobs,
  createScrapeJobApi,
  updateScrapeJobApi,
  startScrapeJob,
  startAllScrapeJobs,
  deleteScrapeJob,
  deleteInventory,
  getAllDealerAssistant,
  getSftpPresetsData
} from '../services/api';
import SearchableDropdown from './SearchableDropdown';
import './Inventory.css';

// CMS Types from backend schema
const CMS_TYPES = [
  { value: 'd2c_media', label: 'D2C Media' },
  { value: 'trader', label: 'Trader' },
  { value: '360_agency', label: '360 Agency' },
  { value: 'powergo', label: 'Powergo' },
  { value: 'magnetis', label: 'Magnetis' },
  { value: 'autoroot', label: 'Autoroot' },
  { value: 'roulottes_gauthier', label: 'Roulottes Gauthier' },
  { value: 'traction_dk', label: 'Traction DK' },
  { value: 'cisss', label: 'CISSS' },
  { value: 'other', label: 'Other' }
];

const Inventory = () => {
  // Sub-tab state
  const [activeTab, setActiveTab] = useState('inventory'); // 'inventory' or 'scrape-jobs'

  // Inventory View State
  const [assistantIds, setAssistantIds] = useState([]);
  const [selectedAssistantId, setSelectedAssistantId] = useState('');
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState('price');
  const [sortOrder, setSortOrder] = useState('asc');

  // URL Check Modal State
  const [urlCheckModalOpen, setUrlCheckModalOpen] = useState(false);
  const [urlCheckLoading, setUrlCheckLoading] = useState(false);
  const [urlCheckResults, setUrlCheckResults] = useState(null);
  const [urlCheckError, setUrlCheckError] = useState('');

  // Scrape Jobs Management State
  const [scrapeJobs, setScrapeJobs] = useState([]);
  const [scrapeJobsLoading, setScrapeJobsLoading] = useState(false);

  // Create Scrape Job Modal State
  const [createJobModalOpen, setCreateJobModalOpen] = useState(false);
  const [createJobLoading, setCreateJobLoading] = useState(false);
  const [createJobValidation, setCreateJobValidation] = useState(false);
  const [newJobData, setNewJobData] = useState({
    assistant_id: '',
    cms_type: '',
    dealer_url: '',
    status: 'active',
    extra_pages: {},
    auto_dealer_doc: false,
    feed_url: '',
    sftp: null
  });
  // Extra pages as array of {key, value} pairs
  const [extraPagesPairs, setExtraPagesPairs] = useState([]);
  const [showSftpConfig, setShowSftpConfig] = useState(false);
  const [sftpConfig, setSftpConfig] = useState({
    host: '',
    user: '',
    password: '',
    path: '',
    port: ''
  });

  // Update Scrape Job Modal State
  const [updateJobModalOpen, setUpdateJobModalOpen] = useState(false);
  const [updateJobLoading, setUpdateJobLoading] = useState(false);
  const [selectedJobForUpdate, setSelectedJobForUpdate] = useState(null);
  const [updateJobData, setUpdateJobData] = useState({
    cms_type: '',
    dealer_url: '',
    status: 'active',
    extra_pages: {}
  });
  // Update modal extra pages as array of {key, value} pairs
  const [updateExtraPagesPairs, setUpdateExtraPagesPairs] = useState([]);

  // Scrape Jobs Filter State
  const [scrapeJobsSearch, setScrapeJobsSearch] = useState('');
  const [scrapeJobsCmsFilter, setScrapeJobsCmsFilter] = useState('');
  const [scrapeJobsStatusFilter, setScrapeJobsStatusFilter] = useState('');

  // Start Scrape Job State
  const [selectedJobToStart, setSelectedJobToStart] = useState('');
  const [startJobLoading, setStartJobLoading] = useState(false);
  const [startAllJobsLoading, setStartAllJobsLoading] = useState(false);

  // Start All Jobs Confirmation Modal State
  const [startAllModalOpen, setStartAllModalOpen] = useState(false);

  // Delete Confirmation Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', assistantId: '', name: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');

  // Dealer Assistants State (for dropdown)
  const [dealerAssistants, setDealerAssistants] = useState([]);
  const [dealerAssistantsLoading, setDealerAssistantsLoading] = useState(false);

  // SFTP Presets State
  const [sftpPresets, setSftpPresets] = useState([]);
  const [selectedSftpPreset, setSelectedSftpPreset] = useState('');

  // Update Modal SFTP State
  const [updateShowSftpConfig, setUpdateShowSftpConfig] = useState(false);
  const [updateSftpConfig, setUpdateSftpConfig] = useState({
    host: '',
    user: '',
    password: '',
    path: '',
    port: ''
  });
  const [updateSelectedSftpPreset, setUpdateSelectedSftpPreset] = useState('');
  const [updateAutoGenDealerDoc, setUpdateAutoGenDealerDoc] = useState(false);
  const [updateFeedUrl, setUpdateFeedUrl] = useState('');

  // Fetch assistant IDs on component mount
  useEffect(() => {
    const fetchAssistantIds = async () => {
      setLoading(true);
      try {
        const response = await listAssistantIds();
        if (response.data.status === 'success') {
          setAssistantIds(response.data.assistant_ids);
        } else {
          setError('Failed to load assistant IDs');
        }
      } catch (err) {
        setError(`Error fetching assistant IDs: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAssistantIds();
  }, []);

  // Fetch scrape jobs on component mount
  useEffect(() => {
    fetchScrapeJobs();
  }, []);

  // Fetch dealer assistants on component mount
  useEffect(() => {
    const fetchDealerAssistants = async () => {
      setDealerAssistantsLoading(true);
      try {
        const response = await getAllDealerAssistant();
        // Response structure is response.data.data (array of assistants)
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          // Sort by name for better UX
          const sortedAssistants = [...response.data.data].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '')
          );
          setDealerAssistants(sortedAssistants);
        } else {
          setDealerAssistants([]);
        }
      } catch (err) {
        console.error('Error fetching dealer assistants:', err);
        setDealerAssistants([]);
      } finally {
        setDealerAssistantsLoading(false);
      }
    };

    fetchDealerAssistants();
  }, []);

  // Fetch SFTP presets on component mount
  useEffect(() => {
    const fetchSftpPresets = async () => {
      try {
        const response = await getSftpPresetsData();
        // Handle Firestore document structure: { "360_agency": { host, port, file_path }, ... }
        if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // Convert object to array of presets with id
          const presetsArray = Object.entries(response.data).map(([key, value]) => ({
            id: key,
            name: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), // "360_agency" -> "360 Agency"
            host: value.host || '',
            port: value.port || '',
            path: value.file_path || value.path || '',
            user: value.user || '',
            password: value.password || ''
          }));
          setSftpPresets(presetsArray);
        } else if (response.data && Array.isArray(response.data)) {
          setSftpPresets(response.data);
        } else {
          setSftpPresets([]);
        }
      } catch (err) {
        // API endpoint may not exist yet - fail silently
        if (err.response?.status === 404) {
          console.warn('SFTP presets endpoint not available (v1/data/get_sftp). Presets disabled.');
        } else {
          console.error('Error fetching SFTP presets:', err);
        }
        setSftpPresets([]);
      }
    };

    fetchSftpPresets();
  }, []);

  const fetchScrapeJobs = async () => {
    setScrapeJobsLoading(true);
    try {
      const response = await getAllScrapeJobs();
      if (response.data && Array.isArray(response.data)) {
        setScrapeJobs(response.data);
      } else {
        setScrapeJobs([]);
      }
    } catch (err) {
      console.error('Error fetching scrape jobs:', err);
      setScrapeJobs([]);
    } finally {
      setScrapeJobsLoading(false);
    }
  };

  // Filter scrape jobs based on search and filters
  const getFilteredScrapeJobs = () => {
    return scrapeJobs.filter(job => {
      const assistantId = job.assistant_id || job.id || '';

      // Search by assistant ID
      const matchesSearch = !scrapeJobsSearch ||
        assistantId.toLowerCase().includes(scrapeJobsSearch.toLowerCase());

      // Filter by CMS type
      const matchesCms = !scrapeJobsCmsFilter ||
        job.cms_type === scrapeJobsCmsFilter;

      // Filter by status
      const matchesStatus = !scrapeJobsStatusFilter ||
        job.status === scrapeJobsStatusFilter;

      return matchesSearch && matchesCms && matchesStatus;
    });
  };

  // Get unique CMS types from scrape jobs for filter dropdown
  const getUniqueCmsTypes = () => {
    const cmsTypes = scrapeJobs.map(job => job.cms_type).filter(Boolean);
    return [...new Set(cmsTypes)];
  };

  // Get unique statuses from scrape jobs for filter dropdown
  const getUniqueStatuses = () => {
    const statuses = scrapeJobs.map(job => job.status).filter(Boolean);
    return [...new Set(statuses)];
  };

  // Clear all scrape job filters
  const clearScrapeJobFilters = () => {
    setScrapeJobsSearch('');
    setScrapeJobsCmsFilter('');
    setScrapeJobsStatusFilter('');
  };

  // Fetch inventory when an assistant ID is selected
  useEffect(() => {
    if (!selectedAssistantId) return;

    const fetchInventory = async () => {
      setLoading(true);
      setInventory(null);
      try {
        const response = await getFullInventory(selectedAssistantId);
        if (response.data.status === 'success') {
          setInventory(response.data.inventory);
        } else {
          setError(`Failed to load inventory for ${selectedAssistantId}`);
        }
      } catch (err) {
        setError(`Error fetching inventory: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [selectedAssistantId]);

  // Get unique categories from inventory
  const getUniqueCategories = () => {
    if (!inventory?.data) return [];
    const categories = inventory.data.map(item => item.category).filter(Boolean);
    return [...new Set(categories)];
  };

  // Filter and sort inventory data
  const getFilteredAndSortedInventory = () => {
    if (!inventory?.data) return [];

    let filtered = inventory.data.filter(vehicle => {
      const matchesSearch =
        (!searchTerm) ||
        (vehicle.description && vehicle.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (vehicle.model && vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory =
        (!filterCategory) ||
        (vehicle.category === filterCategory);

      return matchesSearch && matchesCategory;
    });

    return filtered.sort((a, b) => {
      if (!a[sortBy] && !b[sortBy]) return 0;
      if (!a[sortBy]) return sortOrder === 'asc' ? -1 : 1;
      if (!b[sortBy]) return sortOrder === 'asc' ? 1 : -1;

      if (typeof a[sortBy] === 'number' && typeof b[sortBy] === 'number') {
        return sortOrder === 'asc' ? a[sortBy] - b[sortBy] : b[sortBy] - a[sortBy];
      }

      const aValue = String(a[sortBy]).toLowerCase();
      const bValue = String(b[sortBy]).toLowerCase();
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  };

  // Toggle sort order
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // URL Check Modal Functions
  const handleOpenUrlCheckModal = () => {
    setUrlCheckModalOpen(true);
    setUrlCheckResults(null);
    setUrlCheckError('');
    setUrlCheckLoading(true);
    performUrlCheck();
  };

  const handleCloseUrlCheckModal = () => {
    setUrlCheckModalOpen(false);
    setUrlCheckResults(null);
    setUrlCheckError('');
    setUrlCheckLoading(false);
  };

  const performUrlCheck = async () => {
    try {
      const response = await checkInventoryUrls(selectedAssistantId);
      if (response.data.status === 'success') {
        setUrlCheckResults(response.data);
      } else {
        setUrlCheckError('Failed to check URLs');
      }
    } catch (err) {
      setUrlCheckError(`Error checking URLs: ${err.message}`);
    } finally {
      setUrlCheckLoading(false);
    }
  };

  // Get success rate color
  const getSuccessRateColor = (rate) => {
    if (rate >= 80) return '#4caf50';
    if (rate >= 50) return '#ff9800';
    return '#f44336';
  };

  // ============ CREATE SCRAPE JOB FUNCTIONS ============
  const handleOpenCreateJobModal = () => {
    setCreateJobModalOpen(true);
    setCreateJobValidation(false);
    setSelectedSftpPreset('');
    setNewJobData({
      assistant_id: '',
      cms_type: '',
      dealer_url: '',
      status: 'active',
      extra_pages: {},
      auto_dealer_doc: false,
      feed_url: '',
      sftp: null
    });
    setExtraPagesPairs([]);
    setShowSftpConfig(false);
    setSftpConfig({
      host: '',
      user: '',
      password: '',
      path: '',
      port: ''
    });
  };

  const handleCloseCreateJobModal = () => {
    setCreateJobModalOpen(false);
  };

  // Check if assistant_id already exists in scrape jobs
  const checkDuplicateAssistantId = (assistantId) => {
    if (!assistantId) return false;
    return scrapeJobs.some(job =>
      (job.assistant_id === assistantId) || (job.id === assistantId)
    );
  };

  // Handle dealer assistant selection - auto-populate dealer URL
  const handleDealerAssistantChange = (assistantId) => {
    setNewJobData(prev => ({ ...prev, assistant_id: assistantId }));

    // Find the selected dealer assistant and auto-populate the URL
    const selectedAssistant = dealerAssistants.find(
      da => da.assistant_id === assistantId || da.id === assistantId
    );

    if (selectedAssistant && selectedAssistant.dealer_url) {
      setNewJobData(prev => ({ ...prev, dealer_url: selectedAssistant.dealer_url }));
    }
  };

  // Check if current selection is a duplicate
  const isDuplicateAssistant = checkDuplicateAssistantId(newJobData.assistant_id);

  // Handle SFTP preset selection
  const handleSftpPresetChange = (presetId) => {
    setSelectedSftpPreset(presetId);

    if (!presetId) {
      // Reset to empty values if no preset selected
      setSftpConfig({
        host: '',
        user: '',
        password: '',
        path: '',
        port: ''
      });
      return;
    }

    // Find the selected preset and populate SFTP config
    const selectedPreset = sftpPresets.find(p => p.id === presetId);
    if (selectedPreset) {
      setSftpConfig({
        host: selectedPreset.host || '',
        user: selectedPreset.user || '',
        password: selectedPreset.password || '',
        path: selectedPreset.path || '',
        port: selectedPreset.port || 22
      });
    }
  };

  // Handle SFTP preset selection for Update Modal
  const handleUpdateSftpPresetChange = (presetId) => {
    setUpdateSelectedSftpPreset(presetId);

    if (!presetId) {
      setUpdateSftpConfig({
        host: '',
        user: '',
        password: '',
        path: '',
        port: ''
      });
      return;
    }

    const selectedPreset = sftpPresets.find(p => p.id === presetId);
    if (selectedPreset) {
      setUpdateSftpConfig({
        host: selectedPreset.host || '',
        user: selectedPreset.user || '',
        password: selectedPreset.password || '',
        path: selectedPreset.path || '',
        port: selectedPreset.port || 22
      });
    }
  };

  // Convert extra pages pairs array to JSON object
  const convertPairsToObject = (pairs) => {
    const result = {};
    pairs.forEach(pair => {
      if (pair.key && pair.value) {
        const urls = pair.value.split(',').map(url => url.trim()).filter(url => url);
        if (urls.length > 0) {
          result[pair.key] = urls;
        }
      }
    });
    return result;
  };

  // Convert JSON object back to pairs array (for editing existing jobs)
  const convertObjectToPairs = (obj) => {
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj).map(([key, value]) => ({
      key,
      value: Array.isArray(value) ? value.join(', ') : String(value)
    }));
  };

  // Extra pages pair management - Create Modal
  const addExtraPagesPair = () => {
    setExtraPagesPairs([...extraPagesPairs, { key: '', value: '' }]);
  };

  const removeExtraPagesPair = (index) => {
    setExtraPagesPairs(extraPagesPairs.filter((_, i) => i !== index));
  };

  const updateExtraPagesPair = (index, field, value) => {
    const updated = [...extraPagesPairs];
    updated[index][field] = value;
    setExtraPagesPairs(updated);
  };

  // Extra pages pair management - Update Modal
  const addUpdateExtraPagesPair = () => {
    setUpdateExtraPagesPairs([...updateExtraPagesPairs, { key: '', value: '' }]);
  };

  const removeUpdateExtraPagesPair = (index) => {
    setUpdateExtraPagesPairs(updateExtraPagesPairs.filter((_, i) => i !== index));
  };

  const updateUpdateExtraPagesPair = (index, field, value) => {
    const updated = [...updateExtraPagesPairs];
    updated[index][field] = value;
    setUpdateExtraPagesPairs(updated);
  };

  const handleCreateJobSubmit = async (e) => {
    e.preventDefault();
    setCreateJobValidation(true);
    if (!newJobData.assistant_id || !newJobData.cms_type || isDuplicateAssistant) return;
    setCreateJobLoading(true);
    try {
      // Convert key-value pairs to JSON object
      const extraPages = convertPairsToObject(extraPagesPairs);

      const jobData = {
        ...newJobData,
        extra_pages: extraPages
      };

      if (showSftpConfig && sftpConfig.host) {
        jobData.sftp = sftpConfig;
      }

      await createScrapeJobApi(jobData);
      alert('Scrape job created successfully!');
      handleCloseCreateJobModal();
      fetchScrapeJobs();
    } catch (err) {
      alert(`Error creating scrape job: ${err.response?.data?.detail || err.message}`);
    } finally {
      setCreateJobLoading(false);
    }
  };

  // ============ UPDATE SCRAPE JOB FUNCTIONS ============
  const handleOpenUpdateJobModal = (job) => {
    setSelectedJobForUpdate(job);
    setUpdateJobData({
      cms_type: job.cms_type || '',
      dealer_url: job.dealer_url || '',
      status: job.status || 'active',
      extra_pages: job.extra_pages || {}
    });

    // Convert extra_pages JSON object to key-value pairs for editing
    const pairs = convertObjectToPairs(job.extra_pages);
    setUpdateExtraPagesPairs(pairs);

    // Set auto_dealer_doc and feed_url
    setUpdateAutoGenDealerDoc(job.auto_dealer_doc || false);
    setUpdateFeedUrl(job.feed_url || '');

    // Set SFTP config if exists
    if (job.sftp) {
      setUpdateShowSftpConfig(true);
      setUpdateSftpConfig({
        host: job.sftp.host || '',
        user: job.sftp.user || '',
        password: job.sftp.password || '',
        path: job.sftp.path || '',
        port: job.sftp.port || 22
      });
      setUpdateSelectedSftpPreset('');
    } else {
      setUpdateShowSftpConfig(false);
      setUpdateSftpConfig({
        host: '',
        user: '',
        password: '',
        path: '',
        port: ''
      });
      setUpdateSelectedSftpPreset('');
    }

    setUpdateJobModalOpen(true);
  };

  const handleCloseUpdateJobModal = () => {
    setUpdateJobModalOpen(false);
    setSelectedJobForUpdate(null);
  };

  const handleUpdateJobSubmit = async (e) => {
    e.preventDefault();
    if (!selectedJobForUpdate) return;

    setUpdateJobLoading(true);
    try {
      // Convert key-value pairs to JSON object
      const extraPages = convertPairsToObject(updateExtraPagesPairs);

      const updates = {
        ...updateJobData,
        extra_pages: extraPages,
        auto_dealer_doc: updateAutoGenDealerDoc,
        feed_url: updateFeedUrl
      };

      // Add SFTP config if enabled
      if (updateShowSftpConfig && updateSftpConfig.host) {
        updates.sftp = updateSftpConfig;
      }

      await updateScrapeJobApi(selectedJobForUpdate.assistant_id || selectedJobForUpdate.id, updates);
      alert('Scrape job updated successfully!');
      handleCloseUpdateJobModal();
      fetchScrapeJobs();
    } catch (err) {
      alert(`Error updating scrape job: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUpdateJobLoading(false);
    }
  };

  // ============ START SCRAPE JOB FUNCTIONS ============
  const handleStartScrapeJob = async () => {
    if (!selectedJobToStart) {
      alert('Please select a scrape job to start');
      return;
    }

    setStartJobLoading(true);
    try {
      await startScrapeJob(selectedJobToStart);
      alert(`Scrape job started successfully for ${selectedJobToStart}`);
      setSelectedJobToStart('');
    } catch (err) {
      alert(`Error starting scrape job: ${err.response?.data?.detail || err.message}`);
    } finally {
      setStartJobLoading(false);
    }
  };

  // ============ START ALL JOBS FUNCTIONS ============
  const handleOpenStartAllModal = () => {
    setStartAllModalOpen(true);
  };

  const handleCloseStartAllModal = () => {
    setStartAllModalOpen(false);
  };

  const handleStartAllJobs = async () => {
    setStartAllJobsLoading(true);
    try {
      const activeJobsCount = scrapeJobs.filter(job => job.status === 'active').length;
      await startAllScrapeJobs();
      alert(`Successfully started ${activeJobsCount} scrape jobs!`);
      handleCloseStartAllModal();
    } catch (err) {
      alert(`Error starting all scrape jobs: ${err.response?.data?.detail || err.message}`);
    } finally {
      setStartAllJobsLoading(false);
    }
  };

  // ============ DELETE FUNCTIONS ============
  const handleOpenDeleteModal = (type, assistantId) => {
    setDeleteTarget({ type, assistantId, name: assistantId });
    setDeleteConfirmInput('');
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteTarget({ type: '', assistantId: '', name: '' });
    setDeleteConfirmInput('');
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmInput !== deleteTarget.assistantId) {
      alert('Please type the assistant ID correctly to confirm deletion');
      return;
    }

    setDeleteLoading(true);
    try {
      if (deleteTarget.type === 'scrape_job') {
        await deleteScrapeJob(deleteTarget.assistantId);
        alert('Scrape job deleted successfully!');
        fetchScrapeJobs();
      } else if (deleteTarget.type === 'inventory') {
        await deleteInventory(deleteTarget.assistantId);
        alert('Inventory deleted successfully!');
        const response = await listAssistantIds();
        if (response.data.status === 'success') {
          setAssistantIds(response.data.assistant_ids);
        }
        if (selectedAssistantId === deleteTarget.assistantId) {
          setSelectedAssistantId('');
          setInventory(null);
        }
      } else if (deleteTarget.type === 'both') {
        await deleteScrapeJob(deleteTarget.assistantId);
        await deleteInventory(deleteTarget.assistantId);
        alert('Scrape job and inventory deleted successfully!');
        fetchScrapeJobs();
        const response = await listAssistantIds();
        if (response.data.status === 'success') {
          setAssistantIds(response.data.assistant_ids);
        }
        if (selectedAssistantId === deleteTarget.assistantId) {
          setSelectedAssistantId('');
          setInventory(null);
        }
      }
      handleCloseDeleteModal();
    } catch (err) {
      alert(`Error deleting: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Render a single vehicle card
  const renderVehicleCard = (vehicle) => {
    return (
      <div className="vehicle-card" key={vehicle.vehicle_id}>
        {vehicle.image_url && (
          <div className="vehicle-image">
            <img src={vehicle.image_url} alt={vehicle.description || 'Vehicle'} />
          </div>
        )}
        <div className="vehicle-info">
          <h3>{vehicle.model} {vehicle.version ? `- ${vehicle.version}` : ''}</h3>
          <div className="vehicle-price">
            {vehicle.price ? `$${vehicle.price.toLocaleString()}` : 'Price not available'}
          </div>
          <div className="vehicle-details">
            <p>{vehicle.exterior_color} | {vehicle.interior_color}</p>
            <p>{vehicle.transmission} | {vehicle.fuel} | {vehicle.drivetrain}</p>
            <p>Stock #: {vehicle.stock_number}</p>
            <p>Category: {vehicle.category}</p>
            {vehicle.kilometers > 0 && <p>Kilometers: {vehicle.kilometers.toLocaleString()}</p>}
          </div>
          {vehicle.url && (
            <a href={vehicle.url} target="_blank" rel="noopener noreferrer" className="view-details-btn">
              View Details
            </a>
          )}
        </div>
      </div>
    );
  };

  // Get filtered scrape jobs
  const filteredScrapeJobs = getFilteredScrapeJobs();

  // Get active jobs count for display (from all jobs, not filtered)
  const activeJobsCount = scrapeJobs.filter(job => job.status === 'active').length;

  // Check if any filters are active
  const hasActiveFilters = scrapeJobsSearch || scrapeJobsCmsFilter || scrapeJobsStatusFilter;

  return (
    <div className="inventory-container">
      <h1>Inventory Management</h1>

      {/* Sub-tabs Navigation */}
      <div className="sub-tabs">
        <button
          className={`sub-tab ${activeTab === 'scrape-jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('scrape-jobs')}
        >
          Scrape Jobs
          <span className="tab-badge">{scrapeJobs.length}</span>
        </button>
        <button
          className={`sub-tab ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          Inventory View
          <span className="tab-badge">{assistantIds.length}</span>
        </button>
      </div>

      {/* ==================== SCRAPE JOBS TAB ==================== */}
      {activeTab === 'scrape-jobs' && (
        <div className="tab-content">
          <div className="scrape-jobs-management">
            <div className="scrape-jobs-header">
              <h2>Scrape Jobs Management</h2>
              <div className="header-actions">
                <button className="action-btn create-btn" onClick={handleOpenCreateJobModal}>
                  + Create Scrape Job
                </button>
                <button
                  className="action-btn start-all-btn"
                  onClick={handleOpenStartAllModal}
                  disabled={activeJobsCount === 0}
                >
                  Start All Jobs ({activeJobsCount})
                </button>
              </div>
            </div>

            {/* Scrape Jobs Filters */}
            <div className="scrape-jobs-filters">
              <div className="filter-row">
                <div className="filter-group search-group">
                  <label>Search Assistant ID</label>
                  <input
                    type="text"
                    placeholder="Type to search..."
                    value={scrapeJobsSearch}
                    onChange={(e) => setScrapeJobsSearch(e.target.value)}
                    className="filter-input"
                  />
                </div>
                <div className="filter-group cms-group">
                  <label htmlFor="scrape-cms">CMS Type</label>
                  <SearchableDropdown
                    value={scrapeJobsCmsFilter}
                    onChange={setScrapeJobsCmsFilter}
                    id="scrape-cms"
                    options={[{ key: 'All', value: '' }, ...getUniqueCmsTypes().map(cms => ({ key: CMS_TYPES.find(c => c.value === cms)?.label || cms, value: cms }))]}
                  />
                </div>
                <div className="filter-group status-group">
                  <label htmlFor="scrape-status">Status</label>
                  <SearchableDropdown
                    value={scrapeJobsStatusFilter}
                    onChange={setScrapeJobsStatusFilter}
                    id="scrape-status"
                    options={[{ key: 'All', value: '' }, ...getUniqueStatuses().map(status => ({ key: status.charAt(0).toUpperCase() + status.slice(1), value: status }))]}
                  />
                </div>
                <div className="filter-group start-group">
                  <label htmlFor="start-job">Start Single Job</label>
                  <SearchableDropdown
                    value={selectedJobToStart}
                    onChange={setSelectedJobToStart}
                    disabled={scrapeJobsLoading || startJobLoading}
                    id="start-job"
                    options={[{ key: 'Select job...', value: '' }, ...filteredScrapeJobs.filter(job => job.status === 'active').map(job => ({ key: job.assistant_id || job.id, value: job.assistant_id || job.id }))]}
                  />
                </div>
                <button
                  className="action-btn start-btn filter-start-btn"
                  onClick={handleStartScrapeJob}
                  disabled={!selectedJobToStart || startJobLoading}
                >
                  {startJobLoading ? '...' : 'Start'}
                </button>
              </div>
              <div className="filter-summary">
                <span>
                  Showing {filteredScrapeJobs.length} of {scrapeJobs.length} jobs
                  {hasActiveFilters && <span className="filter-active-indicator"> (filtered)</span>}
                </span>
                {hasActiveFilters && (
                  <button className="link-btn" onClick={clearScrapeJobFilters}>
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            {/* Scrape Jobs List */}
            <div className="scrape-jobs-list">
              {scrapeJobsLoading ? (
                <p>Loading scrape jobs...</p>
              ) : scrapeJobs.length === 0 ? (
                <p className="no-data-message">No scrape jobs found. Create one to get started.</p>
              ) : filteredScrapeJobs.length === 0 ? (
                <p className="no-data-message">No scrape jobs match your filters. <button className="link-btn" onClick={clearScrapeJobFilters}>Clear filters</button></p>
              ) : (
                <table className="scrape-jobs-table">
                  <thead>
                    <tr>
                      <th>Assistant ID</th>
                      <th>CMS Type</th>
                      <th>Dealer URL</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredScrapeJobs.map(job => (
                      <tr key={job.assistant_id || job.id}>
                        <td>{job.assistant_id || job.id}</td>
                        <td>{CMS_TYPES.find(c => c.value === job.cms_type)?.label || job.cms_type}</td>
                        <td>
                          <a href={job.dealer_url} target="_blank" rel="noopener noreferrer">
                            {job.dealer_url?.substring(0, 40)}...
                          </a>
                        </td>
                        <td>
                          <span className={`status-badge ${job.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                            {job.status}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <button
                            className="table-action-btn edit-btn"
                            onClick={() => handleOpenUpdateJobModal(job)}
                          >
                            Edit
                          </button>
                          <button
                            className="table-action-btn delete-btn"
                            onClick={() => handleOpenDeleteModal('scrape_job', job.assistant_id || job.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== INVENTORY VIEW TAB ==================== */}
      {activeTab === 'inventory' && (
        <div className="tab-content">
          {/* Assistant Selection */}
          <div className="assistant-selector">
            <label htmlFor="assistant-select">Select Dealer:</label>
            <SearchableDropdown
              id="assistant-select"
              value={selectedAssistantId}
              onChange={setSelectedAssistantId}
              disabled={loading || assistantIds.length === 0}
              options={[{ key: '-- Select a dealer --', value: '' }, ...assistantIds.map(id => ({ key: id, value: id }))]}
            />
            {selectedAssistantId && (
              <button
                className="delete-inventory-btn"
                onClick={() => handleOpenDeleteModal('both', selectedAssistantId)}
              >
                Delete Inventory & Job
              </button>
            )}
          </div>

          {/* Error Message */}
          {error && <div className="error-message">{error}</div>}

          {/* Loading Indicator */}
          {loading && <div className="loading-indicator">Loading...</div>}

          {/* Inventory Content */}
          {inventory && !loading && (
            <div className="inventory-content">
              <div className="inventory-header">
                <h2>
                  {inventory.dealer_url ? (
                    <a href={inventory.dealer_url} target="_blank" rel="noopener noreferrer">
                      {inventory.dealer_url.replace(/(^\w+:|^)\/\//, '').replace(/\/$/, '')}
                    </a>
                  ) : (
                    selectedAssistantId
                  )}
                </h2>
                <p>Showing {inventory.data.length} vehicles</p>
                <p>Last updated: {new Date(inventory.start_time).toLocaleString()}</p>
                <button
                  className="check-urls-btn"
                  onClick={handleOpenUrlCheckModal}
                  disabled={urlCheckLoading}
                >
                  Verifier la validite des URLs
                </button>
              </div>

              {/* Filters and Search */}
              <div className="inventory-filters">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Search vehicles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="filter-box">
                  <SearchableDropdown
                    value={filterCategory}
                    onChange={setFilterCategory}
                    id="inventory-category"
                    aria-label="Category"
                    options={[{ key: 'All Categories', value: '' }, ...getUniqueCategories().map(category => ({ key: category, value: category }))]}
                  />
                </div>

                <div className="sort-controls">
                  <label htmlFor="inventory-sort">Sort by:</label>
                  <SearchableDropdown
                    value={sortBy}
                    onChange={handleSort}
                    id="inventory-sort"
                    options={[{ key: 'Price', value: 'price' }, { key: 'Model', value: 'model' }, { key: 'Kilometers', value: 'kilometers' }, { key: 'Year', value: 'year' }]}
                  />
                  <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
                    {sortOrder === 'asc' ? '\u2191' : '\u2193'}
                  </button>
                </div>
              </div>

              {/* Vehicle Grid */}
              <div className="vehicle-grid">
                {getFilteredAndSortedInventory().length > 0 ? (
                  getFilteredAndSortedInventory().map(vehicle => renderVehicleCard(vehicle))
                ) : (
                  <p>No vehicles found matching your search criteria.</p>
                )}
              </div>
            </div>
          )}

          {/* No Inventory Selected Yet */}
          {!inventory && !loading && !error && (
            <div className="no-selection">
              <p>Please select a dealer to view their inventory</p>
            </div>
          )}
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* URL Check Modal */}
      {urlCheckModalOpen && (
        <div className="modal-overlay" onClick={handleCloseUrlCheckModal}>
          <div className="modal-content url-check-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Verification des URLs</h2>
              <button className="modal-close-btn" onClick={handleCloseUrlCheckModal}>
                &times;
              </button>
            </div>

            <div className="url-check-modal-content">
              {urlCheckLoading && (
                <div className="url-check-loading">
                  <div className="spinner"></div>
                  <p>Verification des URLs en cours... Cela peut prendre un moment.</p>
                </div>
              )}

              {urlCheckError && (
                <div className="url-check-error">
                  <p>{urlCheckError}</p>
                </div>
              )}

              {urlCheckResults && !urlCheckLoading && (
                <div className="url-check-results">
                  <div className="results-summary">
                    <div
                      className="success-rate-circle"
                      style={{ borderColor: getSuccessRateColor(urlCheckResults.success_rate) }}
                    >
                      <span
                        className="rate-value"
                        style={{ color: getSuccessRateColor(urlCheckResults.success_rate) }}
                      >
                        {urlCheckResults.success_rate}%
                      </span>
                      <span className="rate-label">Taux de reussite</span>
                    </div>

                    <div className="stats-grid">
                      <div className="stat-card stat-total">
                        <span className="stat-value">{urlCheckResults.total_checked}</span>
                        <span className="stat-label">Total verifies</span>
                      </div>
                      <div className="stat-card stat-valid">
                        <span className="stat-value">{urlCheckResults.total_valid}</span>
                        <span className="stat-label">URLs valides</span>
                      </div>
                      <div className="stat-card stat-failed">
                        <span className="stat-value">{urlCheckResults.total_failed}</span>
                        <span className="stat-label">URLs invalides</span>
                      </div>
                    </div>
                  </div>

                  {urlCheckResults.failed_urls && urlCheckResults.failed_urls.length > 0 && (
                    <div className="failed-urls-section">
                      <h3>URLs invalides ({urlCheckResults.failed_urls.length})</h3>
                      {urlCheckResults.note && (
                        <p className="failed-urls-note">{urlCheckResults.note}</p>
                      )}
                      <div className="failed-urls-list">
                        {urlCheckResults.failed_urls.map((item, index) => (
                          <div key={index} className="failed-url-item">
                            <span className="url-status-badge">{item.status}</span>
                            <a href={item.url} target="_blank" rel="noopener noreferrer">
                              {item.url}
                            </a>
                            <span className="url-note">{item.note}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Scrape Job Modal */}
      {createJobModalOpen && (
        <div className="modal-overlay" onClick={handleCloseCreateJobModal}>
          <div className="modal-content create-job-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Scrape Job</h2>
              <button className="modal-close-btn" onClick={handleCloseCreateJobModal}>
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateJobSubmit} className="modal-form">
              {createJobValidation && (!newJobData.assistant_id || !newJobData.cms_type) && (
                <div className="error-message" role="alert">Please select a dealer assistant and CMS type.</div>
              )}
              {/* Duplicate Warning Banner */}
              {isDuplicateAssistant && (
                <div className="duplicate-warning-banner">
                  <div className="warning-banner-icon">!</div>
                  <div className="warning-banner-content">
                    <strong>Scrape Job Already Exists</strong>
                    <p>A scrape job for this assistant already exists. Please modify or delete the existing job before creating a new one.</p>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="assistant_id">Dealer Assistant *</label>
                <SearchableDropdown
                  id="assistant_id"
                  value={newJobData.assistant_id}
                  onChange={handleDealerAssistantChange}
                  disabled={dealerAssistantsLoading}
                  error={isDuplicateAssistant || (createJobValidation && !newJobData.assistant_id)}
                  options={[{ key: '-- Select Dealer Assistant --', value: '' }, ...dealerAssistants.map(da => ({ key: da.name || da.assistant_id || da.id, value: da.assistant_id || da.id }))]}
                />
                {dealerAssistantsLoading && <span className="loading-hint">Loading assistants...</span>}
              </div>

              <div className="form-group">
                <label htmlFor="dealer_url">Dealer URL *</label>
                <input
                  type="url"
                  id="dealer_url"
                  value={newJobData.dealer_url}
                  onChange={(e) => setNewJobData({...newJobData, dealer_url: e.target.value})}
                  required
                  placeholder="https://www.dealerwebsite.com"
                />
                <span className="field-hint">Auto-populated from selected assistant (editable)</span>
              </div>

              <div className="form-group">
                <label htmlFor="cms_type">CMS Type *</label>
                <SearchableDropdown
                  id="cms_type"
                  value={newJobData.cms_type}
                  onChange={(value) => setNewJobData({...newJobData, cms_type: value})}
                  error={createJobValidation && !newJobData.cms_type}
                  options={[{ key: '-- Select CMS Type --', value: '' }, ...CMS_TYPES.map(cms => ({ key: cms.label, value: cms.value }))]}
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <SearchableDropdown
                  id="status"
                  value={newJobData.status}
                  onChange={(value) => setNewJobData({...newJobData, status: value})}
                  options={[{ key: 'Active', value: 'active' }, { key: 'Inactive', value: 'inactive' }]}
                />
              </div>

              <div className="form-group toggle-group">
                <label className="toggle-label">
                  <span className="toggle-text">Auto Generate Dealer Doc</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={newJobData.auto_dealer_doc}
                      onChange={(e) => setNewJobData({...newJobData, auto_dealer_doc: e.target.checked})}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="feed_url">Feed URL (optional)</label>
                <input
                  type="url"
                  id="feed_url"
                  value={newJobData.feed_url}
                  onChange={(e) => setNewJobData({...newJobData, feed_url: e.target.value})}
                  placeholder="CSV feed URL for TractionDK"
                />
              </div>

              <div className="form-group extra-pages-section">
                <div className="extra-pages-header">
                  <label>Extra Pages <span className="optional-tag">(optional)</span></label>
                  <button
                    type="button"
                    className="btn-add-pair"
                    onClick={addExtraPagesPair}
                  >
                    + Add Page Group
                  </button>
                </div>
                <span className="field-hint">Add key-value pairs where each key maps to comma-separated URLs</span>

                {extraPagesPairs.length === 0 ? (
                  <div className="no-pairs-message">
                    No extra pages configured. Click "Add Page Group" to add one.
                  </div>
                ) : (
                  <div className="extra-pages-pairs">
                    {extraPagesPairs.map((pair, index) => (
                      <div key={index} className="extra-page-row">
                        <div className="pair-inputs">
                          <input
                            type="text"
                            placeholder="Key (e.g., promotions)"
                            value={pair.key}
                            onChange={(e) => updateExtraPagesPair(index, 'key', e.target.value)}
                            className="pair-key-input"
                          />
                          <input
                            type="text"
                            placeholder="URLs (comma-separated)"
                            value={pair.value}
                            onChange={(e) => updateExtraPagesPair(index, 'value', e.target.value)}
                            className="pair-value-input"
                          />
                        </div>
                        <button
                          type="button"
                          className="btn-remove-pair"
                          onClick={() => removeExtraPagesPair(index)}
                          title="Remove this pair"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group toggle-group">
                <label className="toggle-label">
                  <span className="toggle-text">Configure SFTP <span className="optional-tag">(optional)</span></span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={showSftpConfig}
                      onChange={(e) => setShowSftpConfig(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
              </div>

              {showSftpConfig && (
                <div className="sftp-config-section">
                  <h4>SFTP Configuration</h4>
                  <div className="form-group">
                    <label htmlFor="sftp_preset">Load from Preset</label>
                    <SearchableDropdown
                      id="sftp_preset"
                      value={selectedSftpPreset}
                      onChange={handleSftpPresetChange}
                      options={[{ key: '-- Select Preset --', value: '' }, ...sftpPresets.map(preset => ({ key: preset.name || preset.id, value: preset.id }))]}
                    />
                    <span className="field-hint">Select a preset to auto-fill the fields below</span>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="sftp_host">Host</label>
                      <input
                        type="text"
                        id="sftp_host"
                        value={sftpConfig.host}
                        onChange={(e) => setSftpConfig({...sftpConfig, host: e.target.value})}
                        placeholder="sftp.example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="sftp_port">Port</label>
                      <input
                        type="number"
                        id="sftp_port"
                        value={sftpConfig.port}
                        onChange={(e) => setSftpConfig({...sftpConfig, port: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="sftp_user">Username</label>
                      <input
                        type="text"
                        id="sftp_user"
                        value={sftpConfig.user}
                        onChange={(e) => setSftpConfig({...sftpConfig, user: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="sftp_password">Password</label>
                      <input
                        type="password"
                        id="sftp_password"
                        value={sftpConfig.password}
                        onChange={(e) => setSftpConfig({...sftpConfig, password: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="sftp_path">File Path</label>
                    <input
                      type="text"
                      id="sftp_path"
                      value={sftpConfig.path}
                      onChange={(e) => setSftpConfig({...sftpConfig, path: e.target.value})}
                      placeholder="/path/to/inventory.csv"
                    />
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseCreateJobModal}>
                  Cancel
                </button>
                <div className="submit-button-wrapper">
                  <button
                    type="submit"
                    className={`btn-submit ${isDuplicateAssistant ? 'btn-disabled-locked' : ''}`}
                    disabled={createJobLoading || isDuplicateAssistant}
                  >
                    {createJobLoading ? 'Creating...' : 'Create Job'}
                  </button>
                  {isDuplicateAssistant && (
                    <span className="locked-tooltip">
                      Modify or delete the existing scrape job first
                    </span>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Scrape Job Modal */}
      {updateJobModalOpen && selectedJobForUpdate && (
        <div className="modal-overlay" onClick={handleCloseUpdateJobModal}>
          <div className="modal-content update-job-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Update Scrape Job</h2>
              <button className="modal-close-btn" onClick={handleCloseUpdateJobModal}>
                &times;
              </button>
            </div>

            <form onSubmit={handleUpdateJobSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="update_assistant_id">Assistant ID</label>
                <input
                  type="text"
                  id="update_assistant_id"
                  value={selectedJobForUpdate.assistant_id || selectedJobForUpdate.id}
                  disabled
                  className="disabled-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="update_dealer_url">Dealer URL</label>
                <input
                  type="url"
                  id="update_dealer_url"
                  value={updateJobData.dealer_url}
                  onChange={(e) => setUpdateJobData({...updateJobData, dealer_url: e.target.value})}
                  placeholder="https://www.dealerwebsite.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="update_cms_type">CMS Type</label>
                <SearchableDropdown
                  id="update_cms_type"
                  value={updateJobData.cms_type}
                  onChange={(value) => setUpdateJobData({...updateJobData, cms_type: value})}
                  options={[{ key: '-- Select CMS Type --', value: '' }, ...CMS_TYPES.map(cms => ({ key: cms.label, value: cms.value }))]}
                />
              </div>

              <div className="form-group">
                <label htmlFor="update_status">Status</label>
                <SearchableDropdown
                  id="update_status"
                  value={updateJobData.status}
                  onChange={(value) => setUpdateJobData({...updateJobData, status: value})}
                  options={[{ key: 'Active', value: 'active' }, { key: 'Inactive', value: 'inactive' }]}
                />
              </div>

              <div className="form-group toggle-group">
                <label className="toggle-label">
                  <span className="toggle-text">Auto Generate Dealer Doc</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={updateAutoGenDealerDoc}
                      onChange={(e) => setUpdateAutoGenDealerDoc(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="update_feed_url">Feed URL (optional)</label>
                <input
                  type="url"
                  id="update_feed_url"
                  value={updateFeedUrl}
                  onChange={(e) => setUpdateFeedUrl(e.target.value)}
                  placeholder="CSV feed URL for TractionDK"
                />
              </div>

              <div className="form-group extra-pages-section">
                <div className="extra-pages-header">
                  <label>Extra Pages <span className="optional-tag">(optional)</span></label>
                  <button
                    type="button"
                    className="btn-add-pair"
                    onClick={addUpdateExtraPagesPair}
                  >
                    + Add Page Group
                  </button>
                </div>
                <span className="field-hint">Add key-value pairs where each key maps to comma-separated URLs</span>

                {updateExtraPagesPairs.length === 0 ? (
                  <div className="no-pairs-message">
                    No extra pages configured. Click "Add Page Group" to add one.
                  </div>
                ) : (
                  <div className="extra-pages-pairs">
                    {updateExtraPagesPairs.map((pair, index) => (
                      <div key={index} className="extra-page-row">
                        <div className="pair-inputs">
                          <input
                            type="text"
                            placeholder="Key (e.g., promotions)"
                            value={pair.key}
                            onChange={(e) => updateUpdateExtraPagesPair(index, 'key', e.target.value)}
                            className="pair-key-input"
                          />
                          <input
                            type="text"
                            placeholder="URLs (comma-separated)"
                            value={pair.value}
                            onChange={(e) => updateUpdateExtraPagesPair(index, 'value', e.target.value)}
                            className="pair-value-input"
                          />
                        </div>
                        <button
                          type="button"
                          className="btn-remove-pair"
                          onClick={() => removeUpdateExtraPagesPair(index)}
                          title="Remove this pair"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group toggle-group">
                <label className="toggle-label">
                  <span className="toggle-text">Configure SFTP <span className="optional-tag">(optional)</span></span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={updateShowSftpConfig}
                      onChange={(e) => setUpdateShowSftpConfig(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
              </div>

              {updateShowSftpConfig && (
                <div className="sftp-config-section">
                  <h4>SFTP Configuration</h4>
                  <div className="form-group">
                    <label htmlFor="update_sftp_preset">Load from Preset</label>
                    <SearchableDropdown
                      id="update_sftp_preset"
                      value={updateSelectedSftpPreset}
                      onChange={handleUpdateSftpPresetChange}
                      options={[{ key: '-- Select Preset --', value: '' }, ...sftpPresets.map(preset => ({ key: preset.name || preset.id, value: preset.id }))]}
                    />
                    <span className="field-hint">Select a preset to auto-fill the fields below</span>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="update_sftp_host">Host</label>
                      <input
                        type="text"
                        id="update_sftp_host"
                        value={updateSftpConfig.host}
                        onChange={(e) => setUpdateSftpConfig({...updateSftpConfig, host: e.target.value})}
                        placeholder="sftp.example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="update_sftp_port">Port</label>
                      <input
                        type="number"
                        id="update_sftp_port"
                        value={updateSftpConfig.port}
                        onChange={(e) => setUpdateSftpConfig({...updateSftpConfig, port: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="update_sftp_user">Username</label>
                      <input
                        type="text"
                        id="update_sftp_user"
                        value={updateSftpConfig.user}
                        onChange={(e) => setUpdateSftpConfig({...updateSftpConfig, user: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="update_sftp_password">Password</label>
                      <input
                        type="password"
                        id="update_sftp_password"
                        value={updateSftpConfig.password}
                        onChange={(e) => setUpdateSftpConfig({...updateSftpConfig, password: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="update_sftp_path">File Path</label>
                    <input
                      type="text"
                      id="update_sftp_path"
                      value={updateSftpConfig.path}
                      onChange={(e) => setUpdateSftpConfig({...updateSftpConfig, path: e.target.value})}
                      placeholder="/path/to/inventory.csv"
                    />
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseUpdateJobModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={updateJobLoading}>
                  {updateJobLoading ? 'Updating...' : 'Update Job'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Start All Jobs Confirmation Modal */}
      {startAllModalOpen && (
        <div className="modal-overlay" onClick={handleCloseStartAllModal}>
          <div className="modal-content start-all-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header start-all-header">
              <h2>Start All Scrape Jobs</h2>
              <button className="modal-close-btn" onClick={handleCloseStartAllModal}>
                &times;
              </button>
            </div>

            <div className="start-all-modal-content">
              <div className="info-icon">i</div>
              <p className="info-text">
                You are about to start <strong>{activeJobsCount} active scrape jobs</strong>.
              </p>
              <p className="info-subtext">
                This will trigger all active scraping workflows simultaneously.
                Make sure you want to proceed.
              </p>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseStartAllModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-start-all"
                  onClick={handleStartAllJobs}
                  disabled={startAllJobsLoading}
                >
                  {startAllJobsLoading ? 'Starting...' : `Start All ${activeJobsCount} Jobs`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="modal-overlay" onClick={handleCloseDeleteModal}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h2>Confirm Deletion</h2>
              <button className="modal-close-btn" onClick={handleCloseDeleteModal}>
                &times;
              </button>
            </div>

            <div className="delete-modal-content">
              <div className="warning-icon">!</div>
              <p className="warning-text">
                You are about to delete the <strong>{deleteTarget.type === 'both' ? 'scrape job and inventory' : deleteTarget.type.replace('_', ' ')}</strong> for:
              </p>
              <p className="delete-target-name">{deleteTarget.assistantId}</p>
              <p className="warning-subtext">This action cannot be undone.</p>

              <div className="confirm-input-section">
                <label>Type <strong>{deleteTarget.assistantId}</strong> to confirm:</label>
                <input
                  type="text"
                  value={deleteConfirmInput}
                  onChange={(e) => setDeleteConfirmInput(e.target.value)}
                  placeholder="Enter assistant ID"
                  className="confirm-input"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseDeleteModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-delete"
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading || deleteConfirmInput !== deleteTarget.assistantId}
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
